import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Sparkles } from "lucide-react";
// iter87.180.15 — BrutalConfirm for the async restore path (iOS
// Safari drops native confirms inside async handlers).
import { useBrutalConfirm } from "@/components/BrutalConfirm";

/**
 * Iter31p — League Admin "Restore Original Seeding" card.
 *
 * Copies team seeds from the canonical global template onto this league's
 * current tournament. Useful for tournaments that were created BEFORE seeds
 * were added to the template, so the seed-balanced auto-draft works properly.
 *
 * Hidden unless the league has a non-custom tournament and the draft hasn't
 * started yet (seeds only matter pre-draft).
 */
export default function RestoreSeedingCard({ league, tournament, onChanged }) {
    const [busy, setBusy] = useState(false);
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();

    // Visibility gates — don't render at all if it can't do anything useful.
    if (!tournament) return null;
    if (!tournament.template || tournament.template === "custom") return null;
    // iter59 — Per-tournament draft-lock check. The legacy global
    // `league.draft_id` would hide this card across all tournaments
    // in a multi-tournament league once ANY of them drafted.
    if (league?.drafts_by_tournament?.[tournament.id]) return null;

    // Show how many teams currently lack a seed so the admin knows what'll happen.
    const teams = tournament.teams || [];
    const missingSeeds = teams.filter((t) => t.seed === null || t.seed === undefined).length;

    const run = async () => {
        const ok = await brutalConfirm({
            title: "Restore original seeding from the global template?",
            body: "This copies the canonical seed values onto teams that don't have one yet. It won't change teams that already have a seed.",
            confirmLabel: "Restore seeds",
        });
        if (!ok) return;
        setBusy(true);
        try {
            const r = await api.restoreSeedsFromTemplate(league.id);
            const updated = r?.teams_updated ?? 0;
            const unmatched = r?.teams_unmatched_count ?? 0;
            if (updated === 0 && unmatched === 0) {
                toast.success("All teams were already seeded — nothing to update.");
            } else if (updated === 0) {
                toast.warning(`No teams matched the template (${unmatched} team name(s) didn't match). Check that team names are correct.`);
            } else if (unmatched > 0) {
                toast.success(`Restored ${updated} team seed(s). ${unmatched} team(s) didn't match the template — sample: ${(r.teams_unmatched_sample || []).join(", ")}`);
            } else {
                toast.success(`Restored ${updated} team seed(s) from the template.`);
            }
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't restore seeds");
        }
        setBusy(false);
    };

    return (
        <div
            data-testid="restore-seeding-card"
            className="brutal-card p-5 flex flex-wrap items-center justify-between gap-3"
        >
            <div className="flex items-start gap-3 min-w-0">
                <Sparkles className="w-5 h-5 text-cyber shrink-0 mt-0.5" />
                <div className="min-w-0">
                    <h2 className="font-display font-extrabold text-xl uppercase">Restore Original Seeding</h2>
                    <p className="text-xs text-zinc-400 mt-1 leading-relaxed">
                        Copies the canonical seed (pot / bracket rank) onto each team in your tournament from the global <strong className="text-white">{tournament.template.replace("_", " ").toUpperCase()}</strong> template. Seeds help the auto-draft balance teams fairly across players (snake-by-tier) so no one walks away with two top-pot teams. Only matters before the draft starts.
                    </p>
                    {missingSeeds > 0 && (
                        <p data-testid="restore-seeding-missing-count" className="text-[11px] text-heat mt-1 font-bold">
                            {missingSeeds} team{missingSeeds === 1 ? "" : "s"} currently missing a seed.
                        </p>
                    )}
                    {missingSeeds === 0 && (
                        <p className="text-[11px] text-zinc-500 mt-1">
                            All {teams.length} teams already have a seed — clicking will do nothing.
                        </p>
                    )}
                </div>
            </div>
            <button
                data-testid="restore-seeding-btn"
                onClick={run}
                disabled={busy}
                className="brutal-btn-primary text-xs py-2 disabled:opacity-50"
            >
                {busy ? "Restoring..." : "Restore seeding"}
            </button>
            {confirmHost}
        </div>
    );
}
